import React, { useState, useEffect } from 'react';
import { getOrganizations } from '../../services/organizations';
import { Building2, Users, CheckCircle, Plus, AlertCircle, RefreshCw } from 'lucide-react';
import StatCard from '../../components/Cards/StatCard';
import DataTable from '../../components/Tables/DataTable';

const SuperAdminDashboard = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalOrgs: 0, activeOrgs: 0, totalMembers: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getOrganizations();
      const data = Array.isArray(res) ? res.filter(item => item != null) : [];
      setOrganizations(data);

      setStats({
        totalOrgs: data.length,
        activeOrgs: data.filter(o => o?.status?.toLowerCase() === 'active').length,
        totalMembers: 0,
      });
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Organization',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-900">{row?.name || 'Unnamed Organization'}</span>
          <span className="text-xs text-slate-500">{row?.email || 'No email provided'}</span>
        </div>
      )
    },
    {
      key: 'org_code',
      label: 'Code',
      render: (value, row) => (
        <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200">
          {row?.org_code || 'N/A'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => {
        const rawStatus = row?.status;
        if (!rawStatus || typeof rawStatus !== 'string') {
          return (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
              Unknown
            </span>
          );
        }
        const status = rawStatus.toLowerCase();
        const isActive = status === 'active';
        return (
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            isActive
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
            {rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Registered On',
      render: (value, row) => {
        if (!row?.createdAt) return <span className="text-slate-400 italic text-sm">Pending</span>;
        return new Date(row.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      }
    },
  ];

  return (
    <div className="p-4 md:p-8 space-y-8 max-w-7xl mx-auto animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Super Admin</h1>
          <p className="text-slate-500 font-medium mt-1">Platform-wide organization management</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="p-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
            title="Refresh Data"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-indigo-200 active:scale-95">
            <Plus size={20} strokeWidth={3} />
            Add Organization
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Organizations"
          value={stats.totalOrgs}
          icon={<Building2 className="w-6 h-6 text-indigo-600" />}
          className="border-l-4 border-l-indigo-500"
        />
        <StatCard
          title="Active Organizations"
          value={stats.activeOrgs}
          icon={<CheckCircle className="w-6 h-6 text-emerald-600" />}
          className="border-l-4 border-l-emerald-500"
        />
        <StatCard
          title="Total Members"
          value={stats.totalMembers.toLocaleString()}
          icon={<Users className="w-6 h-6 text-orange-600" />}
          className="border-l-4 border-l-orange-500"
        />
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
          <div>
            <h2 className="text-xl font-extrabold text-slate-800">Organization Directory</h2>
            <p className="text-sm text-slate-500">Overview of the last 5 registrations</p>
          </div>
        </div>
        <div className="min-h-[300px] relative">
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm z-10">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-slate-500 font-bold animate-pulse">Syncing with database...</p>
            </div>
          ) : organizations.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">No Organizations Found</h3>
              <p className="text-slate-500 max-w-xs mx-auto">It looks like there aren't any organizations registered in the system yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DataTable
                columns={columns}
                data={organizations.slice(0, 5)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;